import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Grid, Typography, Box,
  Tab, Tabs, Snackbar, CircularProgress, Alert,
  FormControlLabel, Checkbox, IconButton, Tooltip, Button, Chip
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Science as ScienceIcon,
  Work as WorkIcon,
  Add as AddIcon,
  Verified as VerifiedIcon,
  HelpOutline as HelpOutlineIcon,
} from '@mui/icons-material';
import useSWR from 'swr';
import { axiosService, fetcher } from 'shared/helpers/axios';
import { useUserContext } from 'shared/contexts/UserContext';
import { useUser } from 'shared/helpers/axios';
import FollowedEntitiesPage from 'components/social/FollowedEntitiesPage';
// import GuideTour, { useGuideTour } from 'components/common/guide/GuideTour';

// Extracted components
import SearchPanel from './SearchPanel';
import FilterPanel from './FilterPanel';
import UserCard from './UserCard';
import OrganizationCard from './OrganizationCard';
import LabCard from '../lab/LabCard';
import PositionCard from '../positions/PositionCard';
import PositionDialog from '../positions/PositionDialog';
import { CreateLabDialog } from '../lab/LabDialogs';
import ActionDialog from './ActionDialog';
import { calculateProfileCompleteness, COMPLETENESS_THRESHOLD } from './collaborationHelpers';
import MeetingScheduleDialog from "../../common/dialogs/MeetingScheduleDialog";
import InviteToConsortiumDialog from "../../common/dialogs/InviteToConsortiumDialog";
import SuggestEntityDialog from "../../common/dialogs/SuggestEntityDialog";
import PageContainer from 'components/common/layout/PageContainer';
import PageHeader, { HEADER_TABS_SX } from 'components/common/layout/PageHeader';


// Fisher-Yates shuffle with a mulberry32 seed so order is random per mount but stable within a session
function seededShuffle(arr, seed) {
  if (!arr || arr.length <= 1) return arr;
  let s = seed | 0;
  const rand = () => {
    s = (s + 0x6D2B79F5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const result = [...arr];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

// Users taken in rounds, one per organization (their first affiliation) each round, so the first cards come from
// as many organizations as there are. LIST is already in its (random) order; within an organization, and among the
// organizations of a round, that order is kept. People without an affiliation count as one group.
function spreadByOrganization(list) {
  const groups = new Map();
  list.forEach((u) => {
    const key = u.affiliations?.[0]?.id ?? u.affiliations?.[0]?.name ?? 'none';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(u);
  });
  const queues = [...groups.values()];
  const result = [];
  for (let round = 0; result.length < list.length; round++) {
    queues.forEach((q) => { if (q[round]) result.push(q[round]); });
  }
  return result;
}

const COLLAB_GUIDE_STEPS = [
  {
    label: 'Discover',
    title: 'Switch between people and organizations',
    target: 'tabs',
  },
  {
    label: 'Refine',
    title: 'Search for a good match',
    target: 'search',
  },
  {
    label: 'Save time',
    title: 'Keep track of who you follow',
    target: 'following',
  },
];

// the groups of people on the Users tab, in this order
const USER_GROUPS = {
  connections: 'Connections',
  skills: 'Users with matching expertise',
  field: 'Working in your field',
  others: 'All users',
};

const CollaborationHub = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const tabsRef = useRef(null);
  const searchRef = useRef(null);
  const followingRef = useRef(null);
  // const guide = useGuideTour({
  //   storageKey: 'collab.guide.dismissed.v1',
  //   steps: COLLAB_GUIDE_STEPS,
  //   targetRefs: { tabs: tabsRef, search: searchRef, following: followingRef },
  // });
  const shuffleSeedRef = useRef(Math.floor(Math.random() * 2 ** 32));

  // State management
  const [activeTab, setActiveTab] = useState(location.state?.tab ?? 0);
  const [inputValues, setInputValues] = useState({
    search: '',
    affiliation: '',
    keywords: '',
    organization: '',
    orgType: '',
    country: '',
    researchArea: ''
  });
  const [filters, setFilters] = useState({
    search: '',
    affiliation: '',
    keywords: '',
    organization: '',
    orgType: '',
    country: '',
    researchArea: ''
  });
  const [accordionExpanded, setAccordionExpanded] = useState(false);
  const [viewMode, setViewMode] = useState('all');
  const [selectedItems, setSelectedItems] = useState([]);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  // how many results are shown: more load on scroll or with "Show more" (never the total: no counts shown)
  const [shownCount, setShownCount] = useState(30);
  
  // Suggest entity dialog state
  const [suggestDialogOpen, setSuggestDialogOpen] = useState(false);

  // Invite to consortium dialog state
  const [inviteToConsortiumDialogOpen, setInviteToConsortiumDialogOpen] = useState(false);

  // Meeting dialog state
  const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);
  
  const itemsPerPage = 30;

  // showContent starts false to defer rendering heavy results in first commit
  // This allows header and sidebar to render/activate instantly
  const [showContent, setShowContent] = useState(false);
  
  useEffect(() => {
    // Reset on mount, trigger content render after paint
    setShowContent(false);
    const timer = setTimeout(() => setShowContent(true), 0);
    return () => clearTimeout(timer);
  }, []); // Only on mount/unmount

  // Debounce effect - 400ms delay before applying filters
  useEffect(() => {
    const timeout = setTimeout(() => {
      setFilters(inputValues);
      setShownCount(itemsPerPage); // back to the first results when filters change
    }, 400);
    return () => clearTimeout(timeout);
  }, [inputValues]);

  // you: your skills and research fields group the people below; staff see the number of users
  const { user: me } = useUserContext() || {};
  // staff: how many people are on Otable (active accounts, whatever the filters), as a chip on the Users tab
  const { data: allUsers } = useSWR(me?.is_staff ? '/users/?page_size=1' : null, fetcher, { dedupingInterval: 300000 });

  // Data fetching with SWR
  const usersSWR = useSWR(
    activeTab === 0 ? ['/users/', JSON.stringify(filters), verifiedOnly] : null,
    async ([url, filtersJson, verifiedOnlyFlag]) => {
      const f = JSON.parse(filtersJson);
      const params = new URLSearchParams();

      const searchTerms = [f.search, f.keywords].filter(Boolean).join(' ');
      if (searchTerms) params.append('search', searchTerms);

      if (f.affiliation) params.append('affiliation', f.affiliation);
      if (f.researchArea) params.append('research_area', f.researchArea);
      if (f.country) params.append('country', f.country);
      if (verifiedOnlyFlag) params.append('verified_only', 'true');

      const res = await axiosService.get(`${url}?${params.toString()}`);
      return res.data;
    },
    { 
      revalidateOnFocus: false,
      dedupingInterval: 120000, // 2 minutes
      keepPreviousData: true,
    }
  );

  const orgsSWR = useSWR(
    activeTab === 1 ? ['/consortium/organizations/', JSON.stringify(filters)] : null,
    async ([url, filtersJson]) => {
      const f = JSON.parse(filtersJson);
      const params = new URLSearchParams();
      if (f.search) params.append('search', f.search);
      if (f.organization) params.append('name', f.organization);
      if (f.country) params.append('country', f.country);
      if (f.orgType) params.append('org_type', f.orgType);
      
      const res = await axiosService.get(`${url}?${params.toString()}`);
      return res.data;
    },
    { 
      revalidateOnFocus: false,
      dedupingInterval: 120000, // 2 minutes
      keepPreviousData: true,
    }
  );

  const labsSWR = useSWR(
    activeTab === 2 ? ['/consortium/labs/', JSON.stringify(filters)] : null,
    async ([url, filtersJson]) => {
      const f = JSON.parse(filtersJson);
      const params = new URLSearchParams({ page_size: '200' });
      if (f.search) params.append('search', f.search);
      if (f.organization) params.append('org_name', f.organization);
      if (f.keywords) params.append('keywords', f.keywords);
      if (f.country) params.append('country', f.country);
      const res = await axiosService.get(`${url}?${params.toString()}`);
      return res.data?.results ? res.data : { results: res.data || [] };
    },
    { revalidateOnFocus: false, dedupingInterval: 60000, keepPreviousData: true }
  );
  const [createLabOpen, setCreateLabOpen] = useState(false);

  const positionsSWR = useSWR(
    activeTab === 3 ? ['/consortium/positions/', JSON.stringify(filters)] : null,
    async ([url, filtersJson]) => {
      const f = JSON.parse(filtersJson);
      const params = new URLSearchParams({ page_size: '200' });
      if (f.search) params.append('search', f.search);
      if (f.positionKind) params.append('kind', f.positionKind);
      if (f.workMode) params.append('work_mode', f.workMode);
      if (f.country) params.append('country', f.country);
      if (f.mine) params.append('mine', '1');
      const res = await axiosService.get(`${url}?${params.toString()}`);
      return res.data?.results ? res.data : { results: res.data || [] };
    },
    { revalidateOnFocus: false, dedupingInterval: 30000, keepPreviousData: true }
  );
  const [positionDialogOpen, setPositionDialogOpen] = useState(false);

  // Get the appropriate data based on active tab
  const activeSWR = [usersSWR, orgsSWR, labsSWR, positionsSWR][activeTab] || usersSWR;
  const data = activeTab === 0 ? usersSWR.data?.results?.filter(
        u => u.id !== user?.id) : activeSWR.data?.results;
  const isLoading = activeSWR.isLoading;
  const error = activeSWR.error;
  const entityWord = ['users', 'organizations', 'labs', 'positions'][activeTab] || 'results';

  // Shuffle once per mount; seed is stable within the session so page navigation keeps the same order.
  // For users, complete + verified profiles come first (randomized among themselves), then everyone else (also
  // randomized); then they are spread over organizations: one per organization at a time, the best profiles of each
  // first, so the top of the list shows as many organizations as possible.
  const shuffledData = useMemo(() => {
    if (activeTab !== 0 || !data) {
      return seededShuffle(data, shuffleSeedRef.current);
    }
    const top = [];
    const rest = [];
    data.forEach((u) => {
      if (u.is_verified && calculateProfileCompleteness(u) >= COMPLETENESS_THRESHOLD) {
        top.push(u);
      } else {
        rest.push(u);
      }
    });
    const spread = spreadByOrganization([
      ...seededShuffle(top, shuffleSeedRef.current),
      ...seededShuffle(rest, shuffleSeedRef.current + 1),
    ]);
    // then grouped for you: your connections, people with your skills (most in common first), people in your
    // research fields, everyone else (each group keeps the order above)
    const names = (list, pick) => new Set((list || []).map(pick).filter(Boolean).map((n) => n.toLowerCase()));
    const mySkills = names(me?.skills, (s) => s?.skill?.name);
    const myFields = names(me?.research_interests, (r) => r?.category?.name);
    const groups = { connections: [], skills: [], field: [], others: [] };
    spread.forEach((u) => {
      const skills = (u.skills || []).filter((s) => mySkills.has((s?.skill?.name || '').toLowerCase())).length;
      const fields = (u.research_fields || []).filter((f) => myFields.has((f || '').toLowerCase())).length;
      const group = u.follow_state?.state === 'connected' ? 'connections' : skills ? 'skills' : fields ? 'field' : 'others';
      groups[group].push({ ...u, _group: group, _common: skills });
    });
    groups.skills.sort((a, b) => b._common - a._common);
    return [...groups.connections, ...groups.skills, ...groups.field, ...groups.others];
  }, [data, activeTab, me]);

  // the small titles above each group of people (and how many are in it)
  const groupCounts = useMemo(() => (shuffledData || []).reduce((n, u) => ({ ...n, [u._group]: (n[u._group] || 0) + 1 }), {}),
    [shuffledData]);

  // Show spinner in results area when content not ready or data loading
  const showLoading = !showContent || (isLoading && !shuffledData);

  // Calculate pagination
  const paginatedData = shuffledData?.slice(0, shownCount) || [];

  const hasMore = (shuffledData?.length || 0) > shownCount;
  const showMore = useCallback(() => setShownCount((n) => n + itemsPerPage), []);

  // the next results load as the end of the list scrolls into view
  // (an observer, and a check on every scroll of the page or of the layout's scrolling container)
  const moreRef = useRef(null);
  useEffect(() => {
    const el = moreRef.current;
    if (!el || !hasMore) return undefined;
    const near = () => el.getBoundingClientRect().top < window.innerHeight + 600;
    const check = () => { if (near()) showMore(); };
    const observer = typeof IntersectionObserver !== 'undefined'
      ? new IntersectionObserver((entries) => { if (entries.some((e) => e.isIntersecting)) showMore(); },
        { rootMargin: '0px 0px 600px 0px' })
      : null;
    observer?.observe(el);
    document.addEventListener('scroll', check, { capture: true, passive: true });
    window.addEventListener('resize', check);
    check();                 // the list may already end inside the window
    return () => {
      observer?.disconnect();
      document.removeEventListener('scroll', check, { capture: true });
      window.removeEventListener('resize', check);
    };
  }, [hasMore, showMore, shownCount]);

  // Handlers
  const handleTabChange = useCallback((_, newValue) => {
    setActiveTab(newValue);
    setSelectedItems([]);
    setShownCount(itemsPerPage);
    setVerifiedOnly(false);
    setInputValues({
      search: '',
      affiliation: '',
      keywords: '',
      organization: '',
      orgType: '',
      country: '',
      researchArea: ''
    });
    setFilters({
      search: '',
      affiliation: '',
      keywords: '',
      organization: '',
      orgType: '',
      country: '',
      researchArea: ''
    });
  }, []);

  const handleSelectItem = useCallback((itemId) => {
    const item = data?.find(d => d.id === itemId);
    if (!item) return;
    
    setSelectedItems(prev => {
      const isSelected = prev.find(s => s.id === itemId);
      return isSelected ? prev.filter(s => s.id !== itemId) : [...prev, item];
    });
  }, [data]);

  const handleInputChange = useCallback((field, value) => {
    setInputValues(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSearchClick = useCallback(() => {
    setFilters(inputValues);
  }, [inputValues]);

  const clearFilters = useCallback(() => {
    const emptyFilters = {
      search: '',
      affiliation: '',
      keywords: '',
      organization: '',
      orgType: '',
      country: '',
      researchArea: ''
    };
    setInputValues(emptyFilters);
    setFilters(emptyFilters);
    setAccordionExpanded(false);
  }, []);

  const executeAction = async () => {
    try {
      switch (actionType) {
        case 'consortium':
          const consortiumsResponse = await axiosService.get('/consortium/consortiums/');
          const consortiums = consortiumsResponse.data.results;
          
          if (consortiums.length === 0) {
            throw new Error('No consortiums available');
          }
          
          const targetConsortium = consortiums[0];
          let successCount = 0;
          let failCount = 0;
          
          for (const item of selectedItems) {
            try {
              if (activeTab === 0) {
                await axiosService.post('/interact/consortium-invitations/', {
                  consortium: targetConsortium.id,
                  recipient: item.id
                });
                successCount++;
              } else {
                failCount++;
              }
            } catch (error) {
              console.error(`Failed to invite:`, error);
              failCount++;
            }
          }
          
          if (successCount > 0) {
            setSnackbar({ 
              open: true, 
              message: `Successfully sent ${successCount} invitation(s)${failCount > 0 ? ` (${failCount} failed)` : ''}`, 
              severity: successCount === selectedItems.length ? 'success' : 'warning'
            });
          } else {
            throw new Error('All invitations failed');
          }
          break;
        
        case 'suggest':
          setSnackbar({ 
            open: true, 
            message: `Call suggestions prepared for ${selectedItems.length} items. Feature coming soon!`, 
            severity: 'info' 
          });
          break;
        
        case 'invite':
          setSnackbar({ 
            open: true, 
            message: `Platform invitations prepared for ${selectedItems.length} items. Feature coming soon!`, 
            severity: 'info' 
          });
          break;
        
        default:
          break;
      }

      setActionDialogOpen(false);
      setSelectedItems([]);
      setInviteMessage('');
    } catch (error) {
      console.error('Action failed:', error);
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || error.message || 'Action failed', 
        severity: 'error' 
      });
    }
  };

  return (
    <PageContainer width="wide">
      {/* the title row spans the page: tabs after the title, the search at the right */}
      <PageHeader title="Collaboration Hub" sx={{ mb: 2 }} actions={(
        <>
          {activeTab === 2 && (
            <Button size="small" variant="outlined" startIcon={<AddIcon />} sx={{ whiteSpace: 'nowrap' }} onClick={() => setCreateLabOpen(true)}>
              Create lab
            </Button>
          )}
          {activeTab === 3 && (
            <Button size="small" variant="outlined" startIcon={<AddIcon />} sx={{ whiteSpace: 'nowrap' }} onClick={() => setPositionDialogOpen(true)}>
              Post a position
            </Button>
          )}
          {/* only verified members; next to the search */}
          {activeTab === 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={verifiedOnly}
                  onChange={(e) => setVerifiedOnly(e.target.checked)}
                  size="small"
                  icon={<VerifiedIcon sx={{ fontSize: 20, opacity: 0.35 }} />}
                  checkedIcon={<VerifiedIcon sx={{ fontSize: 20, color: '#1976d2' }} />}
                />
              }
              label="Verified only"
              sx={{
                m: 0, mr:1, whiteSpace: 'nowrap',
                color: verifiedOnly ? 'primary.main' : 'text.secondary',
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.95rem !important',
                  fontWeight: verifiedOnly ? 600 : 400,
                },
              }}
            />
          )}
          <Box sx={{ width: 280 }}>
            <SearchPanel compact searchValue={inputValues.search}
              onSearchChange={(value) => handleInputChange('search', value)} activeTab={activeTab} />
          </Box>
        </>
      )}>
        {/* the other tabs in the main text colour (the theme's grey secondary text reads as disabled);
            the active one stays in the primary colour */}
        <Tabs ref={tabsRef} value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" sx={{ ...HEADER_TABS_SX, minWidth: 0 }}>
          <Tab icon={<PersonIcon />} iconPosition="start" sx={{ py: 1.25, minHeight: 'auto' }}
            label={allUsers?.count != null ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                Users
                <Tooltip title="All users on Otable (staff only)">
                  <Chip label={allUsers.count.toLocaleString()} size="small" sx={{ height: 18, fontSize: 11, '& .MuiChip-label': { px: 0.75 } }} />
                </Tooltip>
              </Box>
            ) : 'Users'} />
          <Tab icon={<BusinessIcon />} label="Organizations" iconPosition="start" sx={{ py: 1.25, minHeight: 'auto' }} />
          <Tab icon={<ScienceIcon />} label="Labs" iconPosition="start" sx={{ py: 1.25, minHeight: 'auto' }} />
          <Tab icon={<WorkIcon />} label="Positions" iconPosition="start" sx={{ py: 1.25, minHeight: 'auto' }} />
        </Tabs>
      </PageHeader>

      {/* Main content - always rendered for instant page load */}
      <Grid container spacing={2}>
        {/* Left Column - Collaboration Hub (3/4 width) */}
        <Grid item xs={12} lg={9}>
          {/* Back button with description */}
          {/* <Box display="flex" alignItems="center" gap={2} mb={2}>
           
            <Typography variant="body1" color="text.secondary">
              Discover and connect with researchers, institutions, and organizations
            </Typography>
          </Box> */}

          {/* Title and Tabs */}
          

          {/* Search Panel */}
          <Box ref={searchRef}>
            
            
          <FilterPanel
            expanded={accordionExpanded}
            onExpandChange={() => setAccordionExpanded(!accordionExpanded)}
            activeTab={activeTab}
            inputValues={inputValues}
            onInputChange={handleInputChange}
            onSearchClick={handleSearchClick}
            onClearFilters={clearFilters}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
            
           
          </Box>

          {/* Filter Panel */}
        

          {/* Action Buttons */}
          {/* {paginatedData.length > 0 && (
            <Box display="flex" gap={2} alignItems="center" mb={2}>
              <Button 
                variant="outlined" 
                size="small"
                startIcon={allSelected ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />} 
                onClick={handleSelectAll}
                sx={{ 
                  borderRadius: '20px',
                  textTransform: 'none',
                  fontSize: '0.85rem',
                  minWidth: '140px'
                }}
              >
                {allSelected ? 'Deselect All' : 'Select All'}
              </Button>
              
              {selectedItems.length > 0 && (
                <>   
                <ActionButtons
                          user={user}
                          isOwnProfile={false}
                          setSuggestDialogOpen={setSuggestDialogOpen}
                          setInviteToConsortiumDialogOpen={setInviteToConsortiumDialogOpen}
                          setMeetingDialogOpen={setMeetingDialogOpen}
                          size="xs"
                          fontSize=".9rem"
                          invertColors={false}
                          showLabels={true}
                    />

                    <Typography variant="body2" color="text.secondary"> 
                        ({selectedItems.length}) selected user{selectedItems.length > 1 ? 's' : ''}
                    </Typography>

                </>
              )}
            </Box>
          )} */}

      {/* Results Grid */}
      <Box sx={{ position: 'relative', minHeight: '400px' }}>
        {/* Loading overlay for results area only */}
        {showLoading && (
          <Box 
            sx={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center', 
              alignItems: 'center', 
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              zIndex: 10,
              gap: 2,
              minHeight: '400px'
            }}
          >
            <CircularProgress size={60} thickness={4} />
            <Typography variant="body2" color="text.secondary">
              Loading {entityWord}...
            </Typography>
          </Box>
        )}

        {/* Results content */}
        {/* Results content */}
        {error && (
          <Alert severity="error">
            Failed to load {entityWord}
          </Alert>
        )}

        {!showLoading && !error && data && data.length === 0 && (
          <Box textAlign="center" py={8}>
            <Typography variant="h6" color="text.secondary">
              No {entityWord} found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {activeTab === 2 ? 'Lead a research group? Create its lab page.'
                : activeTab === 3 ? 'No open positions match. Hiring? Post one.' : 'Try adjusting your filters'}
            </Typography>
            {activeTab === 2 && (
              <Button variant="contained" startIcon={<AddIcon />} sx={{ mt: 2 }} onClick={() => setCreateLabOpen(true)}>
                Create a lab
              </Button>
            )}
          </Box>
        )}

        {!showLoading && !error && data && data.length > 0 && (
          <>
            <Box
              sx={{
                display: 'grid',
                // Column count follows the container's actual rendered width
                // (not just the viewport), so an open sidebar eating into
                // that width naturally drops to fewer, still-comfortable
                // columns instead of cramming the same column count into
                // less space.
                // Cards stretch to fill each column and each row, so there are no gaps.
                // On phones: two people per row (their cards are compact), one organisation, lab or position.
                // people's cards are compact: more of them per row
                gridTemplateColumns: { xs: activeTab === 0 ? 'repeat(2, minmax(0, 1fr))' : '1fr',
                  sm: `repeat(auto-fill, minmax(min(100%, ${activeTab === 0 ? 180 : 250}px), 1fr))` },
                gap: { xs: 1, sm: 1.5, md: 2 },
              }}
            >
              {activeTab === 0 ? (
                // Render Users, grouped: a small title across the grid before each group
                paginatedData.map((user, i) => (
                  <React.Fragment key={user.id}>
                    {user._group && user._group !== paginatedData[i - 1]?._group && (
                      <Typography sx={{ gridColumn: '1 / -1', fontSize: 10.5, fontWeight: 600, letterSpacing: 0.6,
                        textTransform: 'uppercase', color: 'text.main', mt: i ? 1 : 0, mb: -0.5, ml:1 }}>
                        {USER_GROUPS[user._group]} 
                        {/* · {groupCounts[user._group]} */}
                      </Typography>
                    )}
                    <UserCard
                      followState={user.follow_state}
                      user={user}
                      isSelected={selectedItems.some(s => s.id === user.id)}
                      onSelect={handleSelectItem}
                      onNavigate={navigate}
                    />
                  </React.Fragment>
                ))
              ) : activeTab === 2 ? (
                paginatedData.map(lab => (
                  <LabCard key={lab.id} lab={lab} onNavigate={navigate} />
                ))
              ) : activeTab === 3 ? (
                paginatedData.map(position => (
                  <PositionCard key={position.id} position={position} onNavigate={navigate} />
                ))
              ) : (
                // Render Organizations - adjusted for left column layout
                paginatedData.map(org => (
                  <OrganizationCard
                    key={org.id}
                    organization={org}
                    isSelected={selectedItems.some(s => s.id === org.id)}
                    onSelect={handleSelectItem}
                    onNavigate={navigate}
                  />
                ))
              )}
            </Box>

            {hasMore && (
              // scrolling near it loads the next results (no click needed)
              <Box ref={moreRef} mt={3} display="flex" justifyContent="center" aria-live="polite">
                <CircularProgress size={22} aria-label="Loading more" />
              </Box>
            )}
          </>
        )}
      </Box>

        </Grid>

        {/* Right Column - Followed Entities (1/4 width) */}
        <Grid item xs={12} lg={3}>
          <Box
            ref={followingRef}
            sx={{
              position: 'sticky',
              top: 80,
              bgcolor: 'background.paper',
              borderRadius: 2,
              mt: 0,   // level with the filters (the title row spans both columns)
              p: 2,
              boxShadow: 2,
              display: { xs: 'none', lg: 'block' }
            }}
          >
            <Typography variant="h6" fontWeight="bold" mb={2} color="text.primary">
              Following
            </Typography>
            <FollowedEntitiesPage embedded={true} compact={true} maxItems={5} />
          </Box>
        </Grid>
      </Grid>

      {/* {guide.open && (
        <GuideTour
          steps={COLLAB_GUIDE_STEPS}
          step={guide.step}
          targetRect={guide.targetRect}
          onNext={guide.next}
          onClose={guide.close}
          ariaLabel="Collaboration Hub guide"
        />
      )} */}

      {/* Action Dialog */}
      <ActionDialog
        open={actionDialogOpen}
        onClose={() => setActionDialogOpen(false)}
        actionType={actionType}
        selectedItems={selectedItems}
        activeTab={activeTab}
        inviteMessage={inviteMessage}
        onInviteMessageChange={setInviteMessage}
        onExecute={executeAction}
      />

      {/* Snackbar */}
      <PositionDialog
        open={positionDialogOpen}
        onClose={() => setPositionDialogOpen(false)}
        onSaved={(p) => { positionsSWR.mutate(); navigate(`/positions/${p.id}/`); }}
      />
      <CreateLabDialog
        open={createLabOpen}
        onClose={() => setCreateLabOpen(false)}
        onCreated={(lab) => { setCreateLabOpen(false); labsSWR.mutate(); navigate(`/lab/${lab.id}/`); }}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>



      {/* Meeting Schedule Dialog */}
      <MeetingScheduleDialog
        open={meetingDialogOpen}
        onClose={() => setMeetingDialogOpen(false)}
        restrictToUsers={[user]}
      />

      {/* Invite to Consortium Dialog */}
      <InviteToConsortiumDialog
        open={inviteToConsortiumDialogOpen}
        onClose={() => setInviteToConsortiumDialogOpen(false)}
        user={user}
      />

      {/* Suggest Entity Dialog */}
      <SuggestEntityDialog
        open={suggestDialogOpen}
        onClose={() => setSuggestDialogOpen(false)}
        user={user}
      />

    </PageContainer>
  );
};

export default CollaborationHub;
